import { EventType } from "@/analytics/analytics-events";
import { SaveExternalAnnotationEventProperties } from "@/analytics/annotation-events-properties";
import { useErrorHandlers } from "@/errors/components/error-handling-context";
import { grey } from "@faro-lotv/flat-ui";
import { Analytics } from "@faro-lotv/foreign-observers";
import { BcfServicesIntegrationType } from "@faro-lotv/service-wires";
import { Divider, Stack } from "@mui/material";
import { useCallback, useEffect, useState, type JSX } from "react";
import { AnnotationTypeDropdownProps } from "./annotation-editor/annotation-editor";
import { AnnotationEditorButtons } from "./annotation-editor/annotation-editor-buttons";
import { AnnotationEditorTypeSelection } from "./annotation-editor/annotation-editor-type-selection";
import { CreateAnnotationFormProps } from "./create-annotation-form";
import { isDocumentApiImplemented } from "./external-annotation-utils";
import { ProcoreAnnotationForm } from "./procore-annotation-form";
import { useAnnotationScreenshot } from "./use-annotation-screenshot";

type ProcoreAnnotationProps = CreateAnnotationFormProps &
  AnnotationTypeDropdownProps & {
    /** The BCF Service integration type that corresponds to the procore annotation to create */
    integrationType:
      | BcfServicesIntegrationType.procoreObservations
      | BcfServicesIntegrationType.procoreRfis;
  };

/**
 * @returns a Procore annotation creation form including the annotation type selection and the title
 */
export function ProcoreAnnotation({
  onClose,
  onSave,
  selectedAnnotationType,
  integrationType,
  onAnnotationTypeChange,
  annotationTypeOptions,
}: ProcoreAnnotationProps): JSX.Element {
  const { handleErrorWithToast } = useErrorHandlers();

  // before fist press of the confirm button, validationRequested is false
  const [validationRequested, setValidationRequested] = useState(false);

  const [isSaving, setIsSaving] = useState(false);
  const [areMandatoryFieldsFilled, setAreMandatoryFieldsFilled] =
    useState(false);
  const [title, setTitle] = useState("");
  const [assignee, setAssignee] = useState<string>("");
  const [status, setStatus] = useState<string>("");
  const [dueDate, setDueDate] = useState<Date>();
  const [description, setDescription] = useState<string>("");
  const [priority, setPriority] = useState<string>("");
  const [topicType, setTopicType] = useState<string>("");
  const [rfiManager, setRfiManager] = useState<string>("");
  const [disabled, setDisabled] = useState(false);

  useEffect(() => {
    // Reset the fields when the annotation type changes
    setTitle("");
    setAssignee("");
    setStatus("");
    setDueDate(undefined);
    setDescription("");
    setPriority("");
    setTopicType("");
    setRfiManager("");
    setAreMandatoryFieldsFilled(false);
    setValidationRequested(false);
  }, [integrationType]);

  const takeScreenshot = useAnnotationScreenshot();
  const createAnnotation = useCallback(async () => {
    Analytics.track<SaveExternalAnnotationEventProperties>(
      EventType.saveExternalAnnotation,
      {
        type:
          integrationType === BcfServicesIntegrationType.procoreRfis
            ? "Procore RFI"
            : "Procore Observation",
      },
    );
    setIsSaving(true);
    try {
      await onSave({
        title,
        assignee,
        description,
        dueDate,
        status,
        externalAnnotationType: integrationType,
        priority,
        topicType,
        procoreRfiCompanionFields: { rfiManager },
        screenshotFile: isDocumentApiImplemented(integrationType)
          ? await takeScreenshot()
          : undefined,
      });
    } catch (error) {
      handleErrorWithToast({ title: "Saving annotation failed", error });
    }
    setIsSaving(false);
  }, [
    onSave,
    title,
    assignee,
    description,
    dueDate,
    status,
    rfiManager,
    integrationType,
    priority,
    topicType,
    handleErrorWithToast,
    takeScreenshot,
  ]);

  const disableControls = useCallback(() => {
    setDisabled(true);
  }, [setDisabled]);

  const enableControls = useCallback(() => {
    setDisabled(false);
  }, [setDisabled]);

  return (
    <Stack
      direction="column"
      sx={{
        width: "522px",
        backgroundColor: grey[1000],
        p: 2,
        borderRadius: "4px",
        pointerEvents: isSaving ? "none" : undefined,
        flexGrow: 1,
        gap: 3,
        // Allow the main content to scroll if there's not enough space
        overflowY: "auto",
        // but do not add an horizontal scrollbar when the main content needs to scroll
        overflowX: "clip",
      }}
    >
      <AnnotationEditorTypeSelection
        value={selectedAnnotationType}
        options={annotationTypeOptions}
        onChange={(e) => onAnnotationTypeChange(e.target.value)}
        disabled={isSaving}
      />
      <Divider sx={{ bgcolor: grey[0] }} />
      <ProcoreAnnotationForm
        allowEdition={!isSaving}
        externalAnnotationType={integrationType}
        title={title}
        assignee={assignee}
        dueDate={dueDate}
        status={status}
        priority={priority}
        topicType={topicType}
        rfiManager={rfiManager}
        disabled={disabled}
        onTitleChange={setTitle}
        onDescriptionChange={setDescription}
        onAssigneeChange={setAssignee}
        onDueDateChange={setDueDate}
        onStatusChange={setStatus}
        onPriorityChange={setPriority}
        onTopicTypeChange={setTopicType}
        onRfiManagerChange={setRfiManager}
        onError={handleErrorWithToast}
        showMandatoryFieldsError={validationRequested}
        setAreMandatoryFieldsFilled={setAreMandatoryFieldsFilled}
        onConnectionTokenExpired={disableControls}
        onConnectionTokenUpdated={enableControls}
      />
      <AnnotationEditorButtons
        disabledConfirmButton={
          (validationRequested && !areMandatoryFieldsFilled) || disabled
        }
        disabled={isSaving}
        showConfirmButtonSpinner={isSaving}
        confirmButtonText="Create"
        onCancelButtonClick={onClose}
        onConfirmButtonClick={() => {
          if (areMandatoryFieldsFilled) {
            createAnnotation();
          } else {
            setValidationRequested(true);
          }
        }}
      />
    </Stack>
  );
}
